/**
 * Authentication utility using Auth0
 * 
 * This class provides a compatibility layer between Auth0 and the existing app structure.
 */

import gql from "graphql-tag";
import apolloClient from "./apollo-client";
import AppConfig from "../app-config";

export const STORAGE_KEY = "userProfile";

// This will be populated by components with Auth0's composable
let auth0Instance = null;

export default class Authentication {
  /**
   * Set the Auth0 instance (called from components that have access to useAuth0)
   */
  static setAuth0Instance(auth0) {
    auth0Instance = auth0;
  }

  /**
   * Get Auth0 instance
   */
  static getAuth0Instance() {
    return auth0Instance;
  }

  /**
   * Get user profile from Auth0
   */
  static userProfile() {
    if (auth0Instance && auth0Instance.user.value) {
      // Map Auth0 user to our expected format
      const auth0User = auth0Instance.user.value;
      return {
        email: auth0User.email,
        givenName: auth0User.given_name || auth0User.name || auth0User.nickname,
        name: auth0User.name,
        picture: auth0User.picture,
        // You'll need to map groups from Auth0 custom claims or roles
        groups: auth0User['https://extractalpha.com/groups'] || [],
        isLoading: false,
      };
    }

    // Fallback to localStorage for backwards compatibility
    let json = localStorage.getItem(STORAGE_KEY);
    if (json) {
      try {
        return JSON.parse(json);
      } catch (e) {
        console.error("Failed to parse JSON", json);
      }
    }

    return undefined;
  }

  /**
   * Determine if the user is authenticated (Auth0)
   */
  static isUserAuthenticated() {
    if (auth0Instance) {
      return auth0Instance.isAuthenticated.value && !auth0Instance.isLoading.value;
    }
    return false;
  }

  /**
   * Determine if the user is a subscriber
   * Note: You'll need to configure Auth0 to include group/role information in the token
   */
  static isUserSubscriber() {
    const profile = this.userProfile();
    if (!profile) return false;

    const SUBSCRIBER_GROUP = "AlphaClub.Subscribers";
    const groups = profile.groups || [];
    const isSubscriber = !!groups.find(group => group.id === SUBSCRIBER_GROUP || group === SUBSCRIBER_GROUP);
    console.debug('isUserSubscriber?', 'groups', groups, 'result', isSubscriber);
    return isSubscriber;
  }

  /**
   * Clear the user's session
   */
  static clearSession() {
    localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Login with Auth0
   */
  static async login() {
    if (auth0Instance) {
      await auth0Instance.loginWithRedirect();
    } else {
      console.error("Auth0 not initialized");
    }
  }

  /**
   * Logout with Auth0
   */
  static async logout() {
    if (auth0Instance) {
      this.clearSession();
      await auth0Instance.logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });
    } else {
      console.error("Auth0 not initialized");
    }
  }

  /**
   * Legacy: Generate the authentication URL
   * @deprecated Use login() method instead
   */
  static authenticationUrl() {
    return `${AppConfig.loginEndpoint}`;
  }

  /**
   * Legacy: Generate the logout URL
   * @deprecated Use logout() method instead
   */
  static logoutUrl() {
    return `${AppConfig.logoutEndpoint}`;
  }

  /**
   * Initialize the user session
   * This can be used to sync Auth0 user data with your backend API if needed
   */
  static async initializeSession() {
    console.debug('initializing session with Auth0....');
    
    if (!auth0Instance || !auth0Instance.isAuthenticated.value) {
      throw new Error("User not authenticated");
    }

    const userProfile = this.userProfile();
    
    // Store in localStorage for backwards compatibility
    localStorage.setItem(STORAGE_KEY, JSON.stringify(userProfile));

    // Optionally sync with your backend API
    try {
      const result = await apolloClient.query({
        query: gql`
          query me {
            me {
              email
              givenName
              groups {
                id
              }
            }
          }
        `,
      });
      
      if (result.data.me) {
        // Merge backend data with Auth0 data
        const mergedProfile = {
          ...userProfile,
          ...result.data.me,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(mergedProfile));
        console.debug('synced user profile with backend', mergedProfile);
        return mergedProfile;
      }
    } catch (err) {
      console.warn('Could not sync with backend API, using Auth0 profile only:', err);
    }

    return userProfile;
  }
}
